import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { SERVICES, CONTACT } from "../../data/content";

const PAGE_SIZE = 8; // 2 filas x 4 columnas

export const Services = () => {
  const [page, setPage] = useState(0);

  const totalPages = Math.ceil(SERVICES.length / PAGE_SIZE);
  const start = page * PAGE_SIZE;
  const current = SERVICES.slice(start, start + PAGE_SIZE);

  const goTo = (p) => {
    const next = Math.max(0, Math.min(totalPages - 1, p));
    setPage(next);
    const el = document.querySelector("#servicios");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section id="servicios" data-testid="services-section" className="max-w-[1400px] mx-auto px-5 md:px-12 py-24 md:py-32">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-14 md:mb-20 items-end">
        <div className="md:col-span-8">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5AB82D]">
            Especialidades
          </span>
          <h2 className="mt-4 font-serif-display text-4xl sm:text-5xl md:text-6xl font-medium tracking-tight text-[#0A1F10] leading-[1.05]">
            Soluciones de fontanería en Huelva
          </h2>
        </div>
        <div className="md:col-span-4 md:text-right">
          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="services-cta"
            className="inline-flex items-center gap-2 text-sm font-bold text-[#0A1F10] hover:text-[#5AB82D] transition-colors"
          >
            Consulta tu caso <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
        {current.map((s, i) => (
          <motion.article
            key={`${start}-${i}`}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: (i % 4) * 0.05 }}
            whileHover={{ y: -6 }}
            className="group"
            data-testid={`service-card-${start + i}`}
          >
            <div className="relative overflow-hidden rounded-sm aspect-[5/4] bg-[#EBF6E4] shadow-[0_10px_30px_-18px_rgba(10,31,16,0.4)]">
              <img
                src={s.image}
                alt={s.title}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
              />
              <span className="absolute inset-0 bg-[#0A1F10]/0 group-hover:bg-[#0A1F10]/15 transition-colors duration-300" />
            </div>
            <h3 className="mt-4 font-serif-display text-xl md:text-2xl font-semibold text-[#0A1F10] leading-tight">
              {s.title}
            </h3>
          </motion.article>
        ))}
      </div>

      {totalPages > 1 && (
        <div data-testid="services-pagination" className="mt-12 flex items-center justify-center gap-2">
          <button
            onClick={() => goTo(page - 1)}
            disabled={page === 0}
            data-testid="services-prev"
            className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-[#0A1F10]/15 text-[#0A1F10] hover:border-[#5AB82D] hover:text-[#5AB82D] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Anterior"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          {Array.from({ length: totalPages }).map((_, p) => (
            <button
              key={p}
              onClick={() => goTo(p)}
              data-testid={`services-page-${p}`}
              className={`w-11 h-11 rounded-full text-sm font-bold transition-colors ${
                p === page
                  ? "bg-[#5AB82D] text-white"
                  : "border border-[#0A1F10]/15 text-[#0A1F10] hover:border-[#5AB82D] hover:text-[#5AB82D]"
              }`}
            >
              {p + 1}
            </button>
          ))}
          <button
            onClick={() => goTo(page + 1)}
            disabled={page === totalPages - 1}
            data-testid="services-next"
            className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-[#0A1F10]/15 text-[#0A1F10] hover:border-[#5AB82D] hover:text-[#5AB82D] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label="Siguiente"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </section>
  );
};
